import { Box, Flex, Text, chakra } from '@chakra-ui/react';
import { X } from 'lucide-react';

interface DepartmentsInputProps {
  departments: string[];
  newDepartment: string;
  onDepartmentChange: (value: string) => void;
  onAddDepartment: () => void;
  onRemoveDepartment: (dept: string) => void;
  disabled?: boolean;
}

export function DepartmentsInput({
  departments,
  newDepartment,
  onDepartmentChange,
  onAddDepartment,
  onRemoveDepartment,
  disabled = false,
}: DepartmentsInputProps) {
  return (
    <Box mb={{ base: '4', md: '0' }}>
      <Flex justify="space-between" align="center" mb="2">
        <chakra.label
          htmlFor="event-departments-input"
          fontSize={{ base: 'sm', md: 'md' }}
          fontWeight="medium"
          color="volyx.text"
        >
          Departments
        </chakra.label>
        <Text as="span" fontSize={{ base: 'xs', md: 'sm' }} color="volyx.textSecondary">
          ({departments.length}/15)
        </Text>
      </Flex>
      <Flex flexWrap="wrap" gap="2" mb="2">
        {departments.map((dept) => (
          <Flex
            key={dept}
            display="inline-flex"
            alignItems="center"
            gap="2"
            px="3"
            py="1.5"
            bg="volyx.lightGray"
            borderRadius="lg"
            fontSize="sm"
            color="volyx.text"
          >
            <Text as="span" fontSize="sm" color="volyx.text">{dept}</Text>
            <chakra.button
              p="0.5"
              _hover={{ bg: 'volyx.mediumGray' }}
              borderRadius="full"
              transition="color 0.15s, background-color 0.15s"
              cursor="pointer"
              display="flex"
              alignItems="center"
              justifyContent="center"
              border="none"
              bg="transparent"
              onClick={() => onRemoveDepartment(dept)}
              disabled={disabled}
            >
              <X size={14} color="var(--color-text-secondary)" />
            </chakra.button>
          </Flex>
        ))}
      </Flex>
      <Flex gap="2">
        <chakra.input
          id="event-departments-input"
          type="text"
          value={newDepartment}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onDepartmentChange(e.target.value)}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onAddDepartment();
            }
          }}
          placeholder="Add department"
          flex="1"
          w="full"
          h="14"
          bg="volyx.lightGray"
          borderRadius="xl"
          border="none"
          px="4"
          fontSize="md"
          fontFamily="body"
          color="volyx.text"
          boxShadow="0 1px 3px rgba(0,0,0,0.08)"
          transition="all 0.2s"
          _focus={{ outline: 'none', boxShadow: '0 0 0 3px rgba(31,41,50,0.1)', transform: 'scale(1.01)' }}
          _disabled={{ opacity: 0.6, cursor: 'not-allowed' }}
          disabled={disabled}
        />
        <chakra.button
          px={{ base: '0', sm: '6' }}
          py={{ base: '0', sm: '3' }}
          borderRadius="xl"
          fontWeight="semibold"
          transition="all 0.3s"
          cursor="pointer"
          border="none"
          outline="none"
          bg="volyx.action"
          color="white"
          boxShadow="0 4px 12px rgba(0,0,0,0.08)"
          _hover={{ bg: 'rgba(0,122,255,0.9)', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', transform: 'translateY(-2px) scale(1.02)' }}
          display="flex"
          alignItems="center"
          justifyContent="center"
          w={{ base: '12', sm: 'auto' }}
          css={{ '@media (max-width: 375px)': { aspectRatio: '1' } }}
          onClick={onAddDepartment}
          disabled={disabled || !newDepartment.trim()}
        >
          <Box as="span" display={{ base: 'inline-block', sm: 'none' }} fontSize="2xl" lineHeight="none">
            +
          </Box>
          <Box as="span" display={{ base: 'none', sm: 'inline' }}>Add</Box>
        </chakra.button>
      </Flex>
    </Box>
  );
}
